import { handleAuthExpiry } from './authExpiryHandler';

const PUBLIC_ENDPOINTS = [
    '/api/v2/login',
    '/api/v2/login/resolve',
    '/api/v2/candidat/auth',
    '/api/v2/client/config/whitemark',
    '/api/v2/client/logo',
    '/api/v2/password/change',
    '/api/v2/password/reset-request',
    '/api/v2/password/reset-confirm',
    '/api/v2/user/language',
];

function isPublicEndpoint(url) {
    return PUBLIC_ENDPOINTS.some((e) => url.startsWith(e));
}

// A never-resolving promise: returned instead of throwing once a redirect to the
// login page is in flight, so no rejection or toast surfaces while the page unloads.
function pendingForever() {
    return new Promise(() => {});
}

class ApiService {
    constructor() {}

    buildAuthHeaders(url, extraHeaders = {}) {
        const isPublic = isPublicEndpoint(url);
        if (!window?.etesting?.jwt && !isPublic) {
            const error = new Error('No JWT token found');
            error.code = 'NO_JWT_TOKEN';
            throw error;
        }
        return {
            ...(window?.etesting?.jwt && !isPublic ? { Authorization: `Bearer ${window.etesting.jwt}` } : {}),
            ...extraHeaders,
        };
    }

    async fetchWithHeaders(url, method = 'GET', options = {}) {
        let headers;
        try {
            headers = {
                'Content-Type': 'application/json',
                ...this.buildAuthHeaders(url, options.headers),
            };
        } catch (error) {
            if (handleAuthExpiry(error, url, isPublicEndpoint(url))) {
                return pendingForever();
            }
            throw error;
        }

        const response = await fetch(url, {
            method,
            credentials: 'same-origin',
            headers,
            ...options,
        });

        let jsonResponse;
        try {
            jsonResponse = await response.json();
        } catch (error) {
            jsonResponse = {};
        }

        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}`);

            error.response = jsonResponse;
            error.status = response.status;

            if (handleAuthExpiry(error, url, isPublicEndpoint(url))) {
                return pendingForever();
            }
            throw error;
        }

        return jsonResponse;
    }

    async fetchBlobWithHeaders(url, method = 'GET', options = {}) {
        let headers;
        try {
            headers = this.buildAuthHeaders(url, options.headers);
        } catch (error) {
            if (handleAuthExpiry(error, url, isPublicEndpoint(url))) {
                return pendingForever();
            }
            throw error;
        }

        const response = await fetch(url, {
            method,
            credentials: 'same-origin',
            headers,
            ...options,
        });

        if (!response.ok) {
            let jsonResponse;
            try {
                jsonResponse = await response.json();
            } catch (error) {
                jsonResponse = {};
            }

            const error = new Error(`HTTP ${response.status}`);
            error.response = jsonResponse;
            error.status = response.status;

            if (handleAuthExpiry(error, url, isPublicEndpoint(url))) {
                return pendingForever();
            }
            throw error;
        }

        const blob = await response.blob();
        const filenameMatch = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/);

        return { blob, filename: filenameMatch ? filenameMatch[1] : 'report.pdf' };
    }
}

export default ApiService;
